import { Wifi, WifiOff, Signal, AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { dbm, mbps, uptime, useFreebox, useFreeboxSeries } from '@/lib/freebox'

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

export function Dot({ label, ok }: { label: string; ok: boolean | null }) {
  const color =
    ok == null ? 'bg-muted-foreground/40' : ok ? 'bg-emerald-500' : 'bg-destructive'
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  )
}

export function WanBadge({ box }: { box: { wan_state: string | null; wan_media: string | null; backup_4g: boolean; online: boolean } }) {
  if (!box.online)
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <WifiOff className="h-4 w-4" /> injoignable
      </span>
    )
  if (box.backup_4g)
    return (
      <span className="flex items-center gap-1 text-xs text-amber-500">
        <AlertTriangle className="h-4 w-4" /> Backup 4G ?
      </span>
    )
  if (box.wan_state === 'up')
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-500">
        <Wifi className="h-4 w-4" /> WAN up ({box.wan_media ?? '—'})
      </span>
    )
  return (
    <span className="flex items-center gap-1 text-xs text-destructive">
      <WifiOff className="h-4 w-4" /> WAN {box.wan_state ?? 'down'}
    </span>
  )
}

export function FreeboxCard() {
  const { data, isLoading } = useFreebox()
  const { data: series } = useFreeboxSeries('24h')

  if (isLoading) return <Skeleton className="h-64" />
  const box = data?.boxes[0]
  // No box reporting (role not deployed / pairing not done / stale) → hidden.
  if (!box) return null

  const chart = (series?.points ?? [])
    .filter((p) => p.rate_down_bytes_s != null || p.sfp_pwr_rx_dbm != null)
    .map((p) => ({
      t: p.b,
      down: p.rate_down_bytes_s != null ? Math.round(((p.rate_down_bytes_s * 8) / 1e6) * 10) / 10 : null,
      up: p.rate_up_bytes_s != null ? Math.round(((p.rate_up_bytes_s * 8) / 1e6) * 10) / 10 : null,
      rx: p.sfp_pwr_rx_dbm != null ? Math.round(p.sfp_pwr_rx_dbm * 10) / 10 : null,
      tx: p.sfp_pwr_tx_dbm != null ? Math.round(p.sfp_pwr_tx_dbm * 10) / 10 : null,
    }))

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Signal className="h-4 w-4 text-primary" />
          <Link to="/network" className="hover:underline">
            Internet — {box.source}
          </Link>
        </CardTitle>
        <WanBadge box={box} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Down" value={mbps(box.rate_down_bytes_s)} />
          <Stat label="Up" value={mbps(box.rate_up_bytes_s)} />
          <Stat
            label="SFP Rx"
            value={dbm(box.sfp_pwr_rx_dbm)}
            sub={box.sfp_link === false ? 'link DOWN' : box.sfp_link ? 'link up' : undefined}
          />
          <Stat label="SFP Tx" value={dbm(box.sfp_pwr_tx_dbm)} />
          <Stat
            label="Box"
            value={(() => {
              const cpu = box.temp_cpub_c ?? box.temp_cpum_c
              return cpu != null ? `${cpu.toFixed(0)} °C` : '—'
            })()}
            sub={`up ${uptime(box.uptime_s)}`}
          />
        </div>

        {(box.temp_cpub_c != null || box.temp_cpum_c != null || box.temp_t1_c != null ||
          box.temp_t2_c != null || box.temp_sw_c != null || box.fan_rpm != null) && (
          <div className="text-xs text-muted-foreground">
            Températures box :
            {box.temp_cpub_c != null && <> CPU (SoC) {box.temp_cpub_c.toFixed(0)} °C ·</>}
            {box.temp_cpum_c != null && <> CPU (Marvell) {box.temp_cpum_c.toFixed(0)} °C ·</>}
            {box.temp_sw_c != null && <> switch {box.temp_sw_c.toFixed(0)} °C ·</>}
            {box.temp_t1_c != null && <> carte T1 (zone CPU) {box.temp_t1_c.toFixed(0)} °C ·</>}
            {box.temp_t2_c != null && <> carte T2 (boîtier) {box.temp_t2_c.toFixed(0)} °C ·</>}
            {box.fan_rpm != null && <> ventilateur {box.fan_rpm} rpm</>}
          </div>
        )}

        <div className="rounded-md border border-border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">SFP diagnostics</span>
            <span className="text-xs text-muted-foreground">
              {box.sfp_vendor ?? '—'} {box.sfp_model ?? ''} · s/n {box.sfp_serial ?? '—'}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Dot label="présent" ok={box.sfp_present} />
            <Dot label="alim" ok={box.sfp_alim_ok} />
            <Dot label="signal optique" ok={box.sfp_has_signal} />
            <Dot label="link" ok={box.sfp_link} />
            <Dot label="power report" ok={box.sfp_has_power_report} />
          </div>
          <div className="text-xs text-muted-foreground">
            Rx {dbm(box.sfp_pwr_rx_dbm)} · Tx {dbm(box.sfp_pwr_tx_dbm)} · fw{' '}
            {box.firmware_version ?? '—'} · IPv4 {box.ipv4 ?? '—'}
          </div>
          {box.sfp_stats && (
            <div className="text-xs text-muted-foreground">
              {box.sfp_pwr_rx_dbm != null && (
                <>
                  Marge optique{' '}
                  <span
                    className={
                      box.sfp_pwr_rx_dbm - box.sfp_stats.rx_threshold_dbm < 2
                        ? 'text-amber-500 font-medium'
                        : 'text-emerald-500 font-medium'
                    }
                  >
                    {(box.sfp_pwr_rx_dbm - box.sfp_stats.rx_threshold_dbm).toFixed(1)} dB
                  </span>{' '}
                  (seuil {box.sfp_stats.rx_threshold_dbm.toFixed(0)} dBm) ·{' '}
                </>
              )}
              Rx 24h min {dbm(box.sfp_stats.rx_min_24h)} / moy {dbm(box.sfp_stats.rx_avg_24h)} / max{' '}
              {dbm(box.sfp_stats.rx_max_24h)} · link flaps 24h : {box.sfp_stats.link_flaps_24h}
              {box.sfp_stats.backup_pct_24h != null && (
                <>
                  {' '}· backup 4G : {box.sfp_stats.backup_pct_24h}% (24h)
                  {box.sfp_stats.backup_pct_7d != null && <> / {box.sfp_stats.backup_pct_7d}% (7j)</>}
                </>
              )}
            </div>
          )}
        </div>

        {chart.length > 1 && (
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="t"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleTimeString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  yAxisId="rate"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => `${v} Mb/s`}
                  stroke="hsl(var(--muted-foreground))"
                  width={70}
                />
                <YAxis
                  yAxisId="dbm"
                  orientation="right"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => `${v} dBm`}
                  stroke="hsl(var(--muted-foreground))"
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                />
                <Line yAxisId="rate" type="monotone" dataKey="down" name="Down (Mb/s)" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} connectNulls />
                <Line yAxisId="rate" type="monotone" dataKey="up" name="Up (Mb/s)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1.5} connectNulls />
                <Line yAxisId="dbm" type="monotone" dataKey="rx" name="SFP Rx (dBm)" stroke="hsl(var(--destructive))" dot={false} strokeWidth={1} strokeDasharray="4 4" connectNulls />
                <Line yAxisId="dbm" type="monotone" dataKey="tx" name="SFP Tx (dBm)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1} strokeDasharray="2 4" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
